/**
 * "Listed on" badges — the directory/launch listings EntreDash has EARNED.
 *
 * HOW TO ADD A BADGE (once a listing is live and gives you a badge):
 *   1. Grab the directory's official badge image URL (most give you an <img>
 *      snippet — copy the image src) and your listing's public URL.
 *   2. Add an entry below. `img` renders the directory's own badge image;
 *      omit `img` to fall back to a simple text chip with `name`.
 *   3. That's it — the strip auto-appears on the landing page once this array
 *      has at least one entry (it renders nothing while empty, so there's never
 *      a broken empty band).
 *
 * Curate: keep this to the strongest, most recognizable listings (Product Hunt,
 * There's An AI For That, G2). A wall of obscure badges reads as noise to a
 * skeptical visitor — a handful of credible ones reads as legitimacy.
 *
 * Only add a badge for a listing that is ACTUALLY LIVE. A "Featured on X" badge
 * for a directory you're not really on is a false claim.
 */
export interface ListedOnBadge {
  /** Directory / platform name (also the text-chip fallback + alt text). */
  name: string;
  /** Public URL of your live listing there. */
  href: string;
  /** The directory's official badge image URL. Omit for a text-chip fallback. */
  img?: string;
  /**
   * Override the link's `rel`. Defaults to `"noopener noreferrer"`. Set to
   * `"noopener"` for a partner (e.g. tinyshelf) whose attribution wants the
   * Referer header preserved — `noopener` alone keeps the security benefit
   * without stripping the referrer. Never include nofollow/sponsored/ugc for a
   * badge partner that forbids them.
   */
  rel?: string;
}

// Order = strongest / most recognizable first. Product Hunt leads.
// NOTE: Capterra is surfaced in the /ve hero "Listed on" row (with its real
// logo), not here — this marquee is the fuller directory list.
export const LISTED_ON: ListedOnBadge[] = [
  {
    name: 'Product Hunt',
    href: 'https://www.producthunt.com/products/entredash-spark-to-business?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-entredash-spark-to-business',
    // Product Hunt's live embed SVG. Keeping the `t=` cache-buster from the
    // snippet as-is; PH's widget serves the current featured badge from it.
    img: 'https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1217797&theme=light&t=1786183755472',
  },
  // tinyshelf — moved here from the site footer into the rolling band. Its badge
  // verification requires the link point at https://www.tinyshelf.co with NO
  // rel="nofollow"/"sponsored"/"ugc", served to every visitor on the prerendered
  // homepage — all still satisfied: this marquee renders on `/` inside the
  // prerender snapshot. `rel: 'noopener'` (not the default 'noopener noreferrer')
  // preserves the Referer header for tinyshelf's attribution. The badge image is
  // served verbatim from tinyshelf's own host (the 'dark' variant, which reads on
  // the marquee's light plate). Do NOT add nofollow/sponsored/ugc here.
  {
    name: 'tinyshelf',
    href: 'https://www.tinyshelf.co',
    img: 'https://www.tinyshelf.co/badge/tinyshelf-badge-dark-f4d1216a.svg',
    rel: 'noopener',
  },
  {
    name: "There's An AI For That",
    href: 'https://theresanaiforthat.com/ai/https-htttps-entredash-com/?ref=featured&v=12188824',
    img: 'https://media.theresanaiforthat.com/featured-on-taaft.png?width=600',
  },
  {
    name: 'SourceForge',
    href: 'https://sourceforge.net/software/product/EntreDash/',
    // SourceForge "Customers Love Us" review badge. This is the static image
    // URL that sits behind SourceForge's JS embed snippet — their script does
    // nothing but write this <img> inside an <a>, so we use the image directly:
    // no third-party script on the page, and it prerenders like every other
    // badge. The badge is a self-contained white/orange shield with a gray
    // outline, so it reads on the marquee's light plate. SourceForge's own
    // generated markup uses a plain followed link (no nofollow), so no `rel`
    // override is needed.
    img: 'https://b.sf-syn.com/badge_img/4141299/customers-love-us-white?variant_id=sf',
  },
  {
    name: 'Slashdot',
    href: 'https://slashdot.org/software/p/EntreDash/',
    // Slashdot "Users Love Us" review badge — same SourceForge syndication
    // network as the SourceForge badge (shared b.sf-syn.com host, `variant_id=sd`
    // for Slashdot). Static image behind the JS embed, used directly. Hexagon
    // with a near-white fill, teal Slashdot wordmark and gold stars — reads on
    // the marquee's light plate.
    img: 'https://b.sf-syn.com/badge_img/4141299/users-love-us-new-white?variant_id=sd',
  },
  {
    name: 'Startup Fame',
    href: 'https://startupfa.me/s/entredash?utm_source=entredash.com',
    img: 'https://startupfa.me/badges/featured-badge-small.webp',
  },
  {
    name: 'Uneed',
    href: 'https://www.uneed.best/tool/entredash',
    img: 'https://www.uneed.best/EMBED1B.png',
    // Uneed's embed snippet uses rel="noopener" (not the default
    // noopener noreferrer) so its attribution keeps the Referer header —
    // same reasoning as the tinyshelf entry below.
    rel: 'noopener',
  },
  {
    name: 'SaaSHub',
    href: 'https://www.saashub.com/entredash?utm_source=badge&utm_campaign=badge&utm_content=entredash&badge_variant=color&badge_kind=approved',
    img: 'https://cdn-b.saashub.com/img/badges/approved-color.png?v=1',
  },
  {
    name: 'Top Business Software',
    href: 'https://topbusinesssoftware.com/products/EntreDash/reviews/',
    // Top Business Software "Most Loved" badge — same SourceForge syndication
    // network (b.sf-syn.com host, `variant_id=tbs`). Static image behind the JS
    // embed, used directly. Blue "most loved" heart mark that reads on the
    // marquee's light plate.
    img: 'https://b.sf-syn.com/badge_img/4141299/most-loved?variant_id=tbs',
  },
  {
    name: 'Dang.ai',
    href: 'https://dang.ai/tool/entredash-ai-startup-idea-validation-workspace',
    img: 'https://assets.dang.ai/badges/dang-verified-dark.png',
  },
  {
    name: 'Fazier',
    href: 'https://fazier.com/launches/entredash',
    img: 'https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=12121&badge_type=daily&variant=2&theme=dark',
  },
  {
    name: 'PeerPush',
    href: 'https://peerpush.com/p/entredash',
    img: 'https://peerpush.com/p/entredash/badge.png',
  },
  {
    name: 'Wired Business',
    href: 'https://wired.business',
    img: 'https://wired.business/badge1-light.svg',
  },
  {
    name: 'DailyPings',
    href: 'https://dailypings.com/p/entredash',
    img: 'https://dailypings.com/badge.svg',
  },
  {
    name: 'Acid Tools',
    href: 'https://acidtools.com/ai/entredash',
    img: 'https://acidtools.com/assets/images/badge-dark.png',
  },
  {
    name: 'Good AI Tools',
    href: 'https://goodaitools.com/ai/entredash',
    img: 'https://goodaitools.com/assets/images/badge.png',
  },
  {
    name: 'AI X Collection',
    href: 'https://aixcollection.com/ai/entredash',
    img: 'https://aixcollection.com/assets/images/badge.png',
  },
  {
    name: 'Startup Benchmarks',
    href: 'https://startupbenchmarks.com/product/entredash',
    img: 'https://startupbenchmarks.com/assets/images/badge-dark.png',
  },
  {
    name: 'ScrollLaunch',
    href: 'https://www.scrolllaunch.com/products/entredash?ref=badge',
    img: 'https://www.scrolllaunch.com/api/badge/entredash',
  },
  {
    name: 'Launchpadly',
    href: 'https://launchpadly.co/startup/entredash?ref=badge',
    // 'light' variant = light-background badge, which reads on the dark marquee.
    img: 'https://launchpadly.co/embed/badges/startup/entredash.svg?variant=light',
  },
  {
    name: 'Submit AI Tools',
    href: 'https://submitaitools.org',
    img: 'https://submitaitools.org/static_submitaitools/images/submitaitools.png',
  },
  {
    name: 'AI Directories',
    href: 'https://www.aidirectori.es',
    // Use the 'light-mode' (white-text) variant: our marquee ground is dark, and
    // this directory's naming is inverted — its 'dark-mode' file is dark text
    // that vanishes on dark, while 'light-mode' has white text that reads here.
    img: 'https://cdn.aidirectori.es/ai-tools/badges/light-mode.png',
  },
  {
    name: 'NextBigProduct',
    href: 'https://nextbigproduct.com/product/entredash',
    // NextBigProduct's "Featured on" badge, light theme (reads on the light plate).
    img: 'https://nextbigproduct.com/assets/badge/entredash.svg?theme=light',
  },
];
